/**
 * The React half of the «Отдел» filter (T.Z56, on top of T.Z42's vocabulary) — ONE hook
 * so the surfaces that render the select (venues, dashboard, applications, teachers,
 * «Распределение ЦА», квоты) can never disagree about what the options are.
 *
 * `src/departments.ts` is deliberately framework-free (the `unit` Playwright project
 * imports it in plain Node), so the two VIEWER+ reads the join needs — the venue
 * directory (#6, `managerUserIds`) and the user directory (#25.5, `department`) — live
 * here, beside the other dictionary hooks. Both are cached by TanStack under their own
 * keys: a page that reads them twice (its own combobox + this hook) pays one request.
 *
 * `venues` (optional) scopes the result to a SUBSET of the directory — the dashboard
 * passes the entries of the selected region so the option counts keep promising the
 * venues of that region, exactly like the pre-T.Z56 counts built from the #11 rows did.
 * `undefined` = the whole roster; `null` = "the scoped list is not ready yet" → `counts`
 * is `null` and the options print their bare labels — a select must not promise
 * «0 площадок» for data that merely has not arrived, and it must not silently fall back
 * to the whole roster either (the dashboard's region-scoped counts would lie).
 */

import { useMemo } from 'react';

import { useUserDirectory } from './api/venue-links';
import { useVenueDirectory } from './pages/quotas/dictionary';
import {
  countVenueDepartments,
  departmentBucketsByVenue,
  type DepartmentBucket,
  type DepartmentCounts,
  type DepartmentSource,
  type VenueManagersLike,
} from './departments';

/** What a page needs to render the select AND (client-side) filter its rows. */
export interface DepartmentJoin {
  /** `venueId → bucket`, over the SAME venue list the counts are computed from. */
  buckets: ReadonlyMap<number, DepartmentBucket>;
  /** The option counts; `null` while the directories have not answered (bare labels). */
  counts: DepartmentCounts | null;
}

export function useDepartmentJoin(
  venues?: ReadonlyArray<VenueManagersLike & { id: number }> | null,
): DepartmentJoin {
  const venueDirectory = useVenueDirectory();
  const userDirectory = useUserDirectory();

  const usersById = useMemo(
    () => new Map<number, DepartmentSource>(userDirectory.map((entry) => [entry.id, entry])),
    [userDirectory],
  );

  // Three states, deliberately distinct: `null` venues = "not ready" (bare labels),
  // an explicit array = the caller's scoped list, `undefined` = the whole roster.
  const scopedVenues: ReadonlyArray<VenueManagersLike & { id: number }> | null | undefined =
    venues === null ? null : (venues ?? venueDirectory.data ?? undefined);

  const buckets = useMemo(
    () => departmentBucketsByVenue(scopedVenues ?? [], usersById),
    [scopedVenues, usersById],
  );
  const counts = useMemo<DepartmentCounts | null>(
    () => (scopedVenues === null || scopedVenues === undefined ? null : countVenueDepartments(scopedVenues, usersById)),
    [scopedVenues, usersById],
  );

  return { buckets, counts };
}
